import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { track } from "@/lib/analytics";

// Landing-page infographic that frames the ways to use CLARITY by use case
// before the visitor reaches /pricing. Each tile deep-links to its full
// section there. Ordered by scope of use, not by price. No tier is labeled
// universally recommended; the right choice depends on the matter.
const LADDER_STEPS = [
  {
    key: "solo",
    n: "01",
    anchor: "single-pass",
    name: <>Clarity SOLO<sup className="text-[0.5em] align-super">&trade;</sup></>,
    who: "One scenario, one memorandum. Put CLARITY to work on a single live matter.",
    status: "Available today",
    open: true,
  },
  {
    key: "explore",
    n: "02",
    anchor: "pricing-founder",
    name: <>Clarity EXPLORE<sup className="text-[0.5em] align-super">&trade;</sup></>,
    who: "Ongoing issue mapping for recurring professional matters. 2026 rate, locked.",
    status: "Available today",
    open: true,
  },
  {
    key: "core",
    n: "03",
    anchor: "tier-core",
    name: <>Clarity CORE<sup className="text-[0.5em] align-super">&trade;</sup></>,
    who: "Institutional regulatory intelligence and strategic reporting, with Monthly Clarity-to-Strategy Intelligence and Strategic Report-Outs.",
    status: "Membership open",
    open: true,
  },
  {
    key: "encore",
    n: "04",
    anchor: "tier-encore",
    name: <>Clarity ENCORE<sup className="text-[0.5em] align-super">&trade;</sup></>,
    who: "Customized enterprise positioning, deployed inside your organization.",
    status: "By inquiry",
    open: false,
  },
];

export const LadderAtAGlance = () => {
  return (
    <section
      data-testid="ladder-at-a-glance"
      aria-labelledby="ladder-glance-heading"
      className="mb-16 lg:mb-20"
    >
      <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-blueprint mb-3">
        Four ways to use CLARITY &middot; by use case
      </div>
      <h3
        id="ladder-glance-heading"
        className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight font-semibold leading-tight mb-6 max-w-2xl"
      >
        Choose how to use CLARITY.
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border border-ink bg-white">
        {LADDER_STEPS.map((s, i) => (
          <Link
            key={s.key}
            to={`/pricing#${s.anchor}`}
            data-testid={`ladder-glance-${s.key}`}
            onClick={() => track("ladder_glance_click", { tier: s.key, source: "landing" })}
            className={`group relative block p-5 lg:p-6 border-ink transition-colors hover:bg-paper ${i < 3 ? "border-b lg:border-b-0" : ""} ${i < 2 ? "sm:border-b lg:border-b-0" : "sm:border-b-0"} ${i % 2 === 0 ? "sm:border-r" : "sm:border-r-0"} ${i < 3 ? "lg:border-r" : "lg:border-r-0"}`}
          >
            <div className="flex items-baseline justify-between">
              <span className="font-serif text-4xl font-light tracking-tighter leading-none text-blueprint">{s.n}</span>
              <span className={`font-mono text-[10px] uppercase tracking-[0.18em] ${s.open ? "text-[#FF6900]" : "text-ink/55"}`}>
                {s.status}
              </span>
            </div>
            <div className="font-serif text-lg lg:text-xl tracking-tight font-semibold mt-3 text-ink group-hover:text-blueprint transition-colors">
              {s.name}
            </div>
            <p className="font-mono text-[11px] text-ink/70 mt-2 leading-relaxed">{s.who}</p>
            {/* Connecting arrow between tiles, desktop only */}
            {i < 3 && (
              <span aria-hidden="true" className="hidden lg:flex absolute top-1/2 -right-[9px] -translate-y-1/2 z-10 w-[18px] h-[18px] items-center justify-center bg-white border border-ink rounded-full">
                <ArrowRight size={10} strokeWidth={2} className="text-ink" />
              </span>
            )}
          </Link>
        ))}
      </div>
      <div className="mt-4">
        <Link
          to="/pricing"
          data-testid="ladder-glance-cta"
          onClick={() => track("ladder_glance_click", { tier: "compare", source: "landing" })}
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ink border border-ink bg-white px-5 py-3 hover:bg-ink hover:text-white transition-colors"
        >
          Compare every tier side by side
          <ArrowRight size={14} strokeWidth={1.75} />
        </Link>
      </div>
    </section>
  );
};
